import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  Target,
  BarChart3,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ITRiskAssessment_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            IT Risk Assessment & Treatment Methodology
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Systematic process for identifying, assessing, treating, and
            monitoring IT-related risks to protect infrastructure, data, and
            business operations.
          </p>
        </motion.div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              IT Risk Assessment & Treatment Methodology Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              IT Risk Assessment & Treatment Methodology is a systematic process
              that helps organizations identify, assess, and manage risks
              related to their Information Technology (IT) systems, including
              cybersecurity threats, data breaches, and system vulnerabilities.
              This methodology is essential for ensuring the protection of
              critical IT infrastructure and sensitive data, enabling
              organizations to operate securely and comply with regulatory
              requirements, such as GDPR, HIPAA, ISO 27001, and other
              cybersecurity standards.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              Implementing a comprehensive IT risk assessment process helps
              businesses understand potential cyber risks, evaluate their impact
              and likelihood, and determine the most effective strategies for
              mitigating risks. By applying the risk treatment methodology,
              organizations can prioritize actions, allocate resources
              efficiently, and build a resilient IT environment that safeguards
              their assets, reputation, and customer trust.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is IT Risk Assessment & Treatment Methodology?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              IT Risk Assessment is the process of identifying, evaluating, and
              understanding risks that could affect the confidentiality,
              integrity, and availability of information within an
              organization’s IT infrastructure. It involves the identification
              of potential cyber threats, such as malware, hacking attempts,
              data breaches, and insider threats, along with vulnerabilities in
              IT systems and processes that could be exploited by these threats.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              IT Risk Treatment is the process of addressing these identified
              risks by evaluating available options and applying appropriate
              controls to mitigate or manage the risks effectively. Risk
              treatment involves selecting and implementing preventive measures,
              creating recovery plans, and continuously monitoring and updating
              the risk management approach.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of IT Risk Assessment & Treatment Methodology
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="space-y-8">
            {[
              {
                number: "1",
                title: "Risk Identification",
                desc: "The first step in the methodology involves identifying potential IT risks that could impact the organization’s information and infrastructure. This includes understanding both internal and external threats, such as cyberattacks (e.g., phishing, ransomware, DDoS), data breaches and data leaks, system vulnerabilities (e.g., outdated software, weak passwords), and internal threats, such as employee negligence or malicious actions. Effective risk identification also includes mapping out all IT assets, systems, processes, and data flows to identify areas of exposure.",
              },
              {
                number: "2",
                title: "Risk Assessment and Evaluation",
                desc: "After identifying potential risks, organizations assess the likelihood and impact of each risk. The IT risk assessment considers factors such as likelihood (the probability that a specific risk will occur) and impact (the consequences of the risk, should it occur, e.g., financial loss, regulatory fines, reputational damage). Risks are typically evaluated and ranked based on these two factors, allowing the organization to prioritize which risks need to be addressed first.",
              },
              {
                number: "3",
                title: "Risk Treatment Planning",
                desc: "Once risks are identified and assessed, the next step is to decide how to treat or manage each risk. Risk treatment options include Risk Avoidance (eliminating the risk entirely by changing processes or practices), Risk Reduction (mitigating the likelihood or impact of the risk by implementing controls e.g., firewalls, encryption, access controls), Risk Sharing (transferring part or all of the risk to another party, such as outsourcing certain IT functions or purchasing cyber insurance), and Risk Acceptance (accepting the risk when the cost of mitigation is greater than the potential impact). The treatment plan involves selecting appropriate controls, defining roles and responsibilities, and developing strategies for mitigating or recovering from potential incidents.",
              },
              {
                number: "4",
                title: "Implementation of Risk Treatment Controls",
                desc: "After determining the most effective risk treatment strategies, organizations must implement the necessary security controls and mitigation measures. Common IT risk controls include access control (restricting access to sensitive data and systems to authorized personnel only), network security (implementing firewalls, intrusion detection systems (IDS), and antivirus software), data encryption (protecting data both at rest and in transit to prevent unauthorized access), and incident response planning (preparing a clear set of actions to take in the event of a security breach or system failure).",
              },
              {
                number: "5",
                title: "Monitoring and Review",
                desc: "The final stage in the IT risk assessment and treatment methodology involves continuous monitoring and periodic reviews of the risk treatment measures to ensure they are working effectively. Organizations should regularly monitor for emerging threats (e.g., new cyberattacks or vulnerabilities), review and update security controls and incident response plans as necessary, and conduct regular security audits and penetration testing to evaluate system resilience and identify potential weaknesses. This process ensures that the organization remains agile and responsive to changing risk environments, continuously improving its IT security posture.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="flex items-start gap-6 bg-gray-50 p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-shadow"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-xl">
                  {item.number}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Matters */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why IT Risk Assessment & Treatment Methodology Matters for Your
              Business
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Proactive Risk Management — By identifying potential IT risks early, organizations can take proactive steps to prevent disruptions, cyberattacks, and data breaches, ensuring that critical IT infrastructure remains secure and operational.",
                "Improved Cybersecurity Posture — The methodology helps businesses implement effective cybersecurity measures, such as data protection, network security, and incident response plans, minimizing the risk of data breaches, system failures, and malicious activities.",
                "Cost Savings — Effective risk mitigation helps reduce the financial impact of IT disruptions, such as data loss, recovery costs, legal fees, and reputational damage. By investing in preemptive controls and risk management strategies, organizations can avoid costly incidents.",
                "Compliance with Regulations — Conducting thorough IT risk assessments helps businesses comply with data protection and cybersecurity regulations, such as GDPR, HIPAA, PCI DSS, and other industry-specific requirements, reducing the risk of non-compliance penalties.",
                "Business Continuity — IT risk treatment ensures that an organization is prepared to respond to disruptions, such as cyberattacks, system outages, and data breaches, ensuring the continuity of business operations and minimizing downtime.",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Does Include */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Does IT Risk Assessment & Treatment Methodology Include?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              The methodology includes several key steps designed to identify,
              assess, and treat IT risks:
            </p>
            <div className="space-y-4">
              {[
                "1. Risk Identification — Identify potential IT risks, including external threats, internal vulnerabilities, and risks posed by technological changes or business decisions.",
                "2. Risk Evaluation — Assess the likelihood and impact of each risk on the organization’s IT systems, data security, and business operations.",
                "3. Risk Treatment — Select appropriate treatment strategies to address each identified risk, including risk avoidance, reduction, sharing, or acceptance.",
                "4. Implementation of Controls — Deploy necessary security controls and risk treatment measures, such as data encryption, firewall protections, access control, and incident response plans.",
                "5. Monitoring and Review — Continuously monitor the effectiveness of risk treatment measures and make adjustments based on emerging risks, evolving business needs, and regulatory changes.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of IT Risk Assessment & Treatment Methodology
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Improved IT Security",
                "Compliance Assurance",
                "Reduced Operational Risks",
                "Stronger Incident Response and Recovery",
                "Increased Stakeholder Confidence",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Implement */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Implement IT Risk Assessment & Treatment Methodology
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              To implement an IT risk assessment and treatment methodology,
              follow these key steps:
            </p>
            <div className="space-y-4">
              {[
                "1. Identify and Document IT Risks — Conduct an initial risk identification session to document all potential IT risks, including internal and external factors that could affect your IT systems and data.",
                "2. Evaluate the Likelihood and Impact — Assess each identified risk based on its likelihood and potential impact on business operations, data security, and compliance.",
                "3. Develop a Risk Treatment Plan — Create a risk treatment plan outlining the selected actions for each risk, including the use of preventive controls, corrective measures, or recovery plans.",
                "4. Implement Security Controls — Implement appropriate security controls and risk mitigation strategies to reduce the likelihood and impact of identified risks.",
                "5. Monitor and Review — Continuously monitor the effectiveness of implemented controls, regularly review incident response procedures, and ensure ongoing improvement in risk management practices.",
                "6. Regular Audits and Testing — Conduct regular audits, penetration testing, and vulnerability assessments to verify the effectiveness of your IT risk management and treatment controls.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for IT Risk Assessment & Treatment
              Methodology?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. offers expert IT risk assessment and
              treatment methodology consulting services, helping businesses
              identify, assess, and mitigate IT risks effectively while ensuring
              cybersecurity, data protection, and business continuity.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "IT Risk Identification and Assessment",
                "Risk Treatment and Mitigation Plans",
                "Implementation of Security Controls",
                "Incident Response and Disaster Recovery Planning",
                "Internal Audits and Pre-certification Support",
                "Continuous Monitoring and Improvement",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We help businesses implement effective IT risk management
              practices, ensuring cybersecurity, compliance, and business
              continuity.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ITRiskAssessment_Details;
